import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, MessageCircle, Ghost, ListChecks, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, whatsappLink, snapchatLink } from "../lib/api";
import { useI18n } from "../i18n";

export default function OrderSent() {
  const { t } = useI18n();
  const { state } = useLocation();
  const ref = state?.ref;
  const vendor = state?.vendor || {};
  const message = state?.message || "";
  const phone = state?.phone || "";
  const viaSnap = state?.channel === "snapchat";
  const wa = whatsappLink(vendor.whatsapp || vendor.phone, message);
  const snap = snapchatLink(vendor.snapchat);
  const [refCode, setRefCode] = useState(null);
  const national = !!state?.national;
  const [bank, setBank] = useState(null);
  useEffect(() => { if (national) api.get("/settings/national-payment").then((r) => setBank(r.data)).catch(() => setBank({})); }, [national]);
  const copyVal = (v, label) => Promise.resolve(navigator.clipboard?.writeText(String(v))).then(() => toast.success(`${label} copié`)).catch(console.error);

  const copyMsg = () => {
    Promise.resolve(navigator.clipboard?.writeText(message)).then(() => toast.success(t("os.snapCopied"))).catch(console.error);
  };
  const openSnap = copyMsg;

  useEffect(() => {
    if (phone && phone.trim().length >= 8) {
      api.get("/referral/code", { params: { phone: phone.trim() } }).then((r) => setRefCode(r.data)).catch(console.error);
    }
  }, [phone]);

  const shareText = refCode ? `${t("os.refShareMsg1")} ${refCode.code} ${t("os.refShareMsg2")} https://stuffcbd.fr` : "";

  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      <CheckCircle2 className="h-16 w-16 text-neon-green mx-auto mb-6" />
      <h1 className="font-anton text-4xl uppercase text-white mb-3">{t("os.title1")} <span className="neon-text-green">{t("os.title2")}</span></h1>
      {ref && <p className="text-zinc-400 mb-2">{t("os.ref")} <span className="font-mono text-neon-green">#{ref}</span></p>}
      {national ? (
        <div data-testid="transfer-block" className="text-left border-2 border-neon-green bg-ink-surface p-5 mb-8">
          <p className="font-anton uppercase text-white text-lg mb-1">🏦 Règle ta commande par virement instantané</p>
          <p className="text-zinc-400 text-xs mb-4">Ta commande est <span className="text-neon-magenta font-bold">en attente de paiement</span>. Dès réception du virement, elle est validée et ton colis discret part sous 24/48h. Sans paiement sous {bank?.cancel_hours || 48}h, elle est annulée automatiquement.</p>
          {[["Montant exact", `${Number(state?.total || 0).toFixed(2)} €`, "transfer-amount"],
            ["Libellé / référence à indiquer", state?.payment_ref || `STUFF-${ref}`, "transfer-ref"],
            ["Titulaire du compte", bank?.holder || "STUFF", "transfer-holder"],
            ["IBAN", bank?.iban ? bank.iban.replace(/(.{4})/g, "$1 ").trim() : "IBAN à configurer (contacte-nous sur WhatsApp)", "transfer-iban"],
            ...(bank?.bic ? [["BIC", bank.bic, "transfer-bic"]] : [])].map(([label, val, tid]) => (
            <div key={tid} className="flex items-center justify-between gap-3 border-b border-zinc-800 py-2.5">
              <div className="min-w-0"><p className="text-zinc-500 text-[10px] uppercase font-bold">{label}</p><p data-testid={tid} className="text-white font-mono text-sm break-all">{val}</p></div>
              <button type="button" data-testid={`${tid}-copy`} onClick={() => copyVal(val, label)} className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase font-bold border border-neon-green text-neon-green px-2 py-1.5 hover:bg-neon-green hover:text-black"><Copy className="h-3 w-3" /> Copier</button>
            </div>
          ))}
          <p className="text-zinc-500 text-[11px] mt-3">💡 Virement instantané depuis ton appli bancaire : paiement reçu en quelques minutes. Suivi : <Link to={`/suivi/${ref}`} className="text-neon-green underline">/suivi/{ref}</Link></p>
        </div>
      ) : (
      <p className="text-zinc-400 mb-8">
        {t("os.finalize1")} {vendor.name ? <span className="text-neon-magenta font-semibold">{vendor.name}</span> : t("os.finalize2")} {t("os.finalize3")}
      </p>
      )}

      {viaSnap && message && (
        <div data-testid="snap-recap-box" className="border border-[#FFFC00]/60 bg-ink-surface p-4 mb-4 text-left">
          <p className="flex items-center gap-2 text-[#FFFC00] font-anton uppercase text-sm mb-1"><Ghost className="h-4 w-4" /> {t("os.snapStep")}</p>
          <p className="text-zinc-500 text-xs mb-2">{t("os.snapHint")}</p>
          <pre data-testid="snap-recap-text" className="text-zinc-300 text-[11px] whitespace-pre-wrap font-outfit max-h-40 overflow-y-auto bg-ink border border-zinc-800 p-2.5">{message}</pre>
          <a data-testid="snap-copy-btn" href={snap || "#"} onClick={copyMsg} target={snap ? "_blank" : undefined} rel="noopener noreferrer" className="mt-2 w-full flex items-center justify-center gap-2 bg-[#FFFC00] text-black font-anton uppercase py-3.5 hover:bg-yellow-300 transition-colors"><Copy className="h-4 w-4" /> {t("os.snapCopy")}</a>
        </div>
      )}

      {(wa || snap) && (
        <div className="flex flex-col gap-3 mb-8">
          {wa && (
            <a data-testid="ordersent-whatsapp" href={wa} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-neon-green text-black font-anton uppercase py-4 glow-green hover:bg-neon-greenDark transition-colors">
              <MessageCircle className="h-5 w-5" /> {t("os.wa")}
            </a>
          )}
          {snap && !viaSnap && (
            <a data-testid="ordersent-snapchat" href={snap} onClick={openSnap} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#FFFC00] text-black font-anton uppercase py-4 hover:bg-yellow-300 transition-colors">
              <Ghost className="h-5 w-5" /> {t("os.snap")}
            </a>
          )}
        </div>
      )}

      {refCode && (
        <div data-testid="referral-block" className="border-2 border-neon-magenta bg-ink-surface p-5 mb-8 text-left">
          <p className="flex items-center gap-2 font-anton uppercase text-white mb-1"><Users className="h-5 w-5 text-neon-magenta" /> {t("os.refTitle")}</p>
          <p className="text-zinc-400 text-sm mb-3">{t("os.refText")}</p>
          <div className="flex items-center gap-2">
            <span data-testid="referral-code-display" className="flex-1 text-center font-mono text-xl text-neon-magenta border border-dashed border-neon-magenta/60 py-2 tracking-widest">{refCode.code}</span>
            <button type="button" data-testid="referral-copy" onClick={() => { navigator.clipboard?.writeText(refCode.code); toast.success(t("os.refCopied")); }} className="p-3 border border-zinc-700 text-zinc-300 hover:text-white" title={t("os.refCopied")}><Copy className="h-4 w-4" /></button>
          </div>
          <a data-testid="referral-share-wa" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center justify-center gap-2 bg-neon-magenta text-white font-anton uppercase py-3 hover:bg-neon-purple transition-colors">
            <MessageCircle className="h-4 w-4" /> {t("os.refShare")}
          </a>
          {refCode.credit >= 5 && <p className="text-neon-green text-xs mt-2">🎁 {t("os.refCredit")} {refCode.credit.toFixed(2)}€</p>}
        </div>
      )}

      <div className="flex flex-col gap-3">
        <Link to={ref ? `/suivi/${ref}` : "/fidelite"} data-testid="ordersent-track" className="flex items-center justify-center gap-2 border border-neon-green text-neon-green font-anton uppercase py-3 hover:bg-neon-green hover:text-black transition-colors">
          <ListChecks className="h-5 w-5" /> {t("os.track")}
        </Link>
        <Link to="/" data-testid="order-sent-home" className="inline-block text-zinc-400 hover:text-white uppercase text-sm font-semibold py-2">{t("os.home")}</Link>
      </div>
    </div>
  );
}
